/**
 * `<caos-lookup-field>` — a reference / relationship field control.
 * (Catalog: Fields & input › LookupField — a field pointing at another record — reads as a link, re-points through a typeahead picker.)
 *
 * Read mode renders the linked record as a link (`::part(link)`); edit mode is a
 * typeahead combobox that re-points the reference. The input is a real
 * `role="combobox"` (`aria-expanded`, `aria-controls`, `aria-autocomplete="list"`,
 * `aria-activedescendant`) over a `role="listbox"` of `role="option"`s; Arrow Up/Down
 * roam the active option (via `aria-activedescendant`, so DOM focus stays on the input),
 * Enter selects, Esc closes, Home/End jump, a click selects, click-outside / focus-leave
 * closes. Selecting emits a bubbling, composed `caos-lookup-change` (`{ value, label }`);
 * a clear emits `caos-lookup-clear`.
 *
 * Results are supplied through an INJECTED async seam — `search(query, request) => options` — set
 * as a property; this layer never talks to a server or the access engine directly. That
 * seam is where the app phase wires the real kernel read-predicate source.
 *
 * CAOS-601 — WHAT THE SEARCH IS, IS CONFIGURABLE. It used to be one fixed arrangement: match on
 * whatever the seam felt like, search from the first keystroke, wait 150ms, and show one line and
 * maybe a second. The incumbent's record picker declares two things a lookup needs to be told and
 * ours could not be — which fields the typing is MATCHED against, and which fields are SHOWN on a
 * result — and the reason both matter is the same: a lookup that matches only on a name cannot find
 * a record by its number, and one that shows only a name cannot tell two records with the same name
 * apart. `match-fields` and `display-fields` say those two things; `min-chars` and `search-delay`
 * say when to go and ask at all. Every default is what the control did before, so a lookup that
 * configures nothing behaves exactly as it always has.
 *
 * ACCESS NOTE: the options this control offers MUST ultimately be only the records the
 * current principal can READ (object + record + FLS visibility). That filtering is the
 * injected `search` seam's job — the app binds a read-predicate-scoped data source here;
 * the control itself is deliberately access-agnostic so it stays a pure presentational
 * leaf (no data/access dependency baked in).
 *
 * Styling seams: `::part(label)`, `::part(link)`, `::part(combo)`, `::part(input)`,
 * `::part(icon)`, `::part(clear)`, `::part(listbox)`, `::part(option)`, `::part(message)`.
 */
import { CaosElement, define, makeSheet } from '../base-element.js';
import { deepActiveElement } from '../focus.js';
import { makeIcon } from './icons.js';

export interface CaosLookupOption {
  /** The reference id this option would set. */
  value: string;
  /** The visible display label. */
  label: string;
  /** Optional secondary line (e.g. the object / a disambiguator). */
  sublabel?: string;
  /** Optional href for the READ-mode link. */
  href?: string;
  /**
   * CAOS-601 — the record's other fields, by name, for `display-fields` to choose from. A source
   * that returns them lets the SURFACE decide what distinguishes two records with the same name,
   * instead of the source deciding once for every lookup that will ever use it.
   */
  fields?: Readonly<Record<string, string>>;
}

/**
 * CAOS-601 — what the control is asking for, beyond the text itself.
 *
 * The second argument is optional on the receiving side, so every seam written against the old
 * one-argument shape keeps working untouched.
 */
export interface CaosLookupRequest {
  /** What the person typed, trimmed of nothing — the seam gets it exactly as given. */
  readonly query: string;
  /**
   * The fields the query should be matched against, in the order the surface listed them. Empty
   * means the surface said nothing and the source decides, which is what every source did before.
   */
  readonly matchFields: readonly string[];
}

/** The injected result source. Returns (or resolves to) the options for a query. */
export type CaosLookupSearch = (
  query: string,
  request?: CaosLookupRequest,
) => CaosLookupOption[] | Promise<CaosLookupOption[]>;

/** The delay between the last keystroke and the ask, when the surface does not say. */
const DEFAULT_SEARCH_DELAY = 150;

let uid = 0;

const sheet = makeSheet(`
  :host { display: block; }

  .field { display: flex; flex-direction: column; gap: var(--caos-space-1); }

  .label {
    font-family: var(--caos-font);
    font-size: var(--caos-text-body);
    font-weight: 600;
    color: var(--caos-color-text-muted);
  }
  .label:empty { display: none; }
  .req { color: var(--caos-color-danger); margin-left: 2px; }

  /* ── read mode ── the linked record NAME in the accent (never a uuid). */
  .link {
    font-family: var(--caos-font);
    font-size: var(--caos-text-body);
    line-height: 1.4;
    color: var(--caos-color-accent-strong);
    text-decoration: none;
    cursor: pointer;
    border-radius: var(--caos-radius-sm);
  }
  .link:hover { text-decoration: underline; }
  .link:focus-visible { outline: none; box-shadow: var(--caos-focus-ring); }
  .placeholder { color: var(--caos-color-text-muted); font-size: var(--caos-text-body); }

  /* ── edit mode ── */
  .combo { position: relative; display: block; }

  .icon {
    position: absolute;
    top: 50%;
    left: var(--caos-space-3);
    transform: translateY(-50%);
    width: 16px;
    height: 16px;
    color: var(--caos-color-text-muted);
    pointer-events: none;
  }
  .icon svg { display: block; width: 100%; height: 100%; }

  input {
    width: 100%;
    min-width: 0;
    min-height: 40px;
    padding: var(--caos-space-3) calc(var(--caos-space-5) + var(--caos-space-2)) var(--caos-space-3) calc(var(--caos-space-5) + var(--caos-space-1));
    border-radius: var(--caos-radius);
    border: 1px solid var(--caos-color-border);
    background: var(--caos-color-surface-2);
    color: var(--caos-color-text);
    font-family: var(--caos-font);
    font-size: var(--caos-text-body);
    line-height: 1.4;
    transition: border-color 0.12s ease, box-shadow 0.12s ease, background 0.12s ease;
  }
  input::placeholder { color: var(--caos-color-text-muted); opacity: 0.85; }
  input:hover:not(:disabled):not(:focus) { border-color: color-mix(in srgb, var(--caos-color-accent) 45%, var(--caos-color-border)); }
  /* Focus = accent wash + soft strong border, not a solid ring (Dragon §08). */
  input:focus-visible, input:focus {
    outline: none;
    border-color: var(--caos-color-border-strong);
    box-shadow: inset 0 0 0 100px var(--caos-accent-wash);
  }
  input:disabled { background: var(--caos-color-surface-2); color: var(--caos-color-text-muted); cursor: not-allowed; opacity: var(--caos-state-disabled-opacity); }
  :host([invalid]) input { border-color: var(--caos-color-danger); }
  :host([invalid]) input:focus-visible, :host([invalid]) input:focus {
    border-color: var(--caos-color-danger);
    box-shadow: inset 0 0 0 100px color-mix(in srgb, var(--caos-color-danger) 6%, transparent);
  }

  .clear {
    position: absolute;
    top: 50%;
    right: var(--caos-space-2);
    transform: translateY(-50%);
    display: none;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 24px;
    padding: 0;
    appearance: none;
    background: transparent;
    border: none;
    border-radius: var(--caos-radius-sm);
    color: var(--caos-color-text-muted);
    cursor: pointer;
  }
  .clear svg { display: block; width: 16px; height: 16px; }
  .clear:hover { background: var(--caos-color-surface-2); color: var(--caos-color-text); }
  .clear:focus-visible { outline: none; box-shadow: var(--caos-focus-ring); }
  :host([data-has-value]) .clear { display: inline-flex; }

  .listbox {
    position: absolute;
    /* A NAMED layer, not a number — see the header note on layering (CAOS-586). */
    z-index: var(--caos-layer-dropdown);
    top: calc(100% + var(--caos-space-1));
    left: 0;
    right: 0;
    max-height: 260px;
    overflow-y: auto;
    margin: 0;
    padding: var(--caos-space-1);
    list-style: none;
    background: var(--caos-color-surface);
    border: 1px solid var(--caos-color-border);
    border-radius: var(--caos-radius);
    box-shadow: var(--caos-shadow-float);
  }
  .listbox[hidden] { display: none; }

  .option {
    display: flex;
    flex-direction: column;
    gap: 1px;
    padding: var(--caos-space-2) var(--caos-space-3);
    border-radius: var(--caos-radius-sm);
    cursor: pointer;
  }
  /* selected option: accent mixed INTO the theme surface (not the theme-fixed pale
     accent-soft), so the option's --caos-color-text stays legible in dark too */
  .option[aria-selected="true"] { background: var(--caos-state-selected-bg); }
  .option.active { background: var(--caos-color-surface-2); }
  .option[aria-selected="true"].active { background: color-mix(in srgb, var(--caos-color-accent) 20%, var(--caos-color-surface)); }
  .option .opt-label { font-size: var(--caos-text-row); line-height: 1.35; color: var(--caos-color-text); }
  .option .opt-sub { font-size: 11.5px; line-height: 1.3; color: var(--caos-color-text-muted); }

  .status {
    padding: var(--caos-space-2) var(--caos-space-3);
    font-size: 12.5px;
    color: var(--caos-color-text-muted);
  }

  .message {
    font-family: var(--caos-font);
    font-size: 11.5px;
    line-height: 1.4;
    color: var(--caos-color-text-muted);
  }
  .message:empty { display: none; }
  :host([invalid]) .message { color: var(--caos-color-danger); }

  /* ── Inline (record-detail) variant — the P5b in-place editor: the typeahead input
     with no box, an accent underline, inherits the surrounding font. Boxed input above
     stays the form default. Read mode always shows the record NAME (accent-strong link). */
  :host([inline]) .field { gap: 0; }
  :host([inline]) .label, :host([inline]) .message { display: none; }
  :host([inline]) input {
    min-height: 0;
    padding: 0 var(--caos-space-5) 2px calc(var(--caos-space-4) + 2px);
    border: none;
    border-bottom: 1px solid var(--caos-color-accent);
    border-radius: 0;
    background: transparent;
    box-shadow: none;
    font-family: inherit;
    font-size: inherit;
    color: inherit;
  }
  :host([inline]) .icon { left: 0; }
  :host([inline]) .clear { right: 0; }
  :host([inline]) input:hover:not(:disabled):not(:focus) { border-bottom-color: var(--caos-color-accent); }
  :host([inline]) input:focus-visible, :host([inline]) input:focus {
    border-bottom-color: var(--caos-color-accent);
    background: transparent;
    box-shadow: none;
  }
`);

export class CaosLookupField extends CaosElement {
  static get observedAttributes(): string[] {
    return [
      'mode',
      'label',
      'value',
      'display',
      'href',
      'placeholder',
      'disabled',
      'required',
      'invalid',
      'message',
      // CAOS-601 — what the search IS.
      'match-fields',
      'display-fields',
      'min-chars',
      'search-delay',
    ];
  }

  private labelEl: HTMLLabelElement;
  private reqEl: HTMLSpanElement;
  private readView: HTMLDivElement;
  private linkEl: HTMLAnchorElement;
  private placeholderEl: HTMLSpanElement;
  private combo: HTMLDivElement;
  private input: HTMLInputElement;
  private clearBtn: HTMLButtonElement;
  private listbox: HTMLUListElement;
  private messageEl: HTMLDivElement;

  private _search: CaosLookupSearch | null = null;
  private _options: CaosLookupOption[] = [];
  private optionEls: HTMLLIElement[] = [];
  private activeIndex = -1;
  private searchToken = 0;
  private debounce: ReturnType<typeof setTimeout> | null = null;
  private readonly seq = ++uid;
  private readonly listboxId = `caos-lookup-list-${this.seq}`;

  private onDocPointer = (e: Event): void => {
    if (!e.composedPath().includes(this)) this.close();
  };

  constructor() {
    super(sheet);

    const field = document.createElement('div');
    field.className = 'field';

    this.labelEl = document.createElement('label');
    this.labelEl.className = 'label';
    this.labelEl.setAttribute('part', 'label');
    this.reqEl = document.createElement('span');
    this.reqEl.className = 'req';
    this.reqEl.textContent = '*';
    this.reqEl.hidden = true;
    this.reqEl.setAttribute('aria-hidden', 'true');
    this.labelEl.append(this.reqEl);

    // read view
    this.readView = document.createElement('div');
    this.readView.className = 'read';
    this.linkEl = document.createElement('a');
    this.linkEl.className = 'link';
    this.linkEl.setAttribute('part', 'link');
    this.placeholderEl = document.createElement('span');
    this.placeholderEl.className = 'placeholder';
    this.placeholderEl.textContent = '—';
    this.readView.append(this.linkEl, this.placeholderEl);

    // edit view (combobox)
    this.combo = document.createElement('div');
    this.combo.className = 'combo';
    this.combo.setAttribute('part', 'combo');

    const icon = document.createElement('span');
    icon.className = 'icon';
    icon.setAttribute('part', 'icon');
    icon.setAttribute('aria-hidden', 'true');
    icon.append(makeIcon('search'));

    this.input = document.createElement('input');
    // The combobox needs an id its own label can point at — an accessible name from `aria-label`
    // alone still leaves the control unassociated and unclickable from its label (CAOS-587 review).
    this.input.id = `caos-lookup-input-${this.seq}`;
    this.labelEl.htmlFor = this.input.id;
    this.input.type = 'text';
    this.input.setAttribute('part', 'input');
    this.input.setAttribute('role', 'combobox');
    this.input.setAttribute('aria-autocomplete', 'list');
    this.input.setAttribute('aria-expanded', 'false');
    this.input.setAttribute('aria-controls', this.listboxId);
    this.input.autocomplete = 'off';
    this.input.addEventListener('input', () => this.onInput());
    this.input.addEventListener('keydown', (e) => this.onKeydown(e));
    this.input.addEventListener('focus', () => {
      if (this.input.value.trim().length) this.runSearch(this.input.value);
    });

    this.clearBtn = document.createElement('button');
    this.clearBtn.type = 'button';
    this.clearBtn.className = 'clear';
    this.clearBtn.setAttribute('part', 'clear');
    this.clearBtn.setAttribute('aria-label', 'Clear selection');
    this.clearBtn.append(makeIcon('close'));
    this.clearBtn.addEventListener('click', () => this.clear());

    this.listbox = document.createElement('ul');
    this.listbox.className = 'listbox';
    this.listbox.id = this.listboxId;
    this.listbox.setAttribute('part', 'listbox');
    this.listbox.setAttribute('role', 'listbox');
    this.listbox.hidden = true;

    this.combo.append(icon, this.input, this.clearBtn, this.listbox);

    this.messageEl = document.createElement('div');
    this.messageEl.className = 'message';
    this.messageEl.setAttribute('part', 'message');

    field.append(this.labelEl, this.readView, this.combo, this.messageEl);
    this.root.append(field);

    // close when focus leaves the whole component (pierces shadow via deepActiveElement)
    this.addEventListener('focusout', () => {
      // defer so the new focus target settles, then check the deep active element
      setTimeout(() => {
        const active = deepActiveElement();
        if (!active || !this.contains(active) && !this.shadowContains(active)) this.close();
      }, 0);
    });
  }

  connectedCallback(): void {
    this.sync();
  }

  disconnectedCallback(): void {
    document.removeEventListener('pointerdown', this.onDocPointer, true);
    if (this.debounce) clearTimeout(this.debounce);
  }

  attributeChangedCallback(): void {
    this.sync();
  }

  /** The injected result source (data in — the app binds an access-scoped source here). */
  get search(): CaosLookupSearch | null {
    return this._search;
  }
  set search(fn: CaosLookupSearch | null) {
    this._search = fn;
  }

  /** The selected reference id. */
  get value(): string {
    return this.getAttribute('value') ?? '';
  }
  set value(v: string) {
    if (v) this.setAttribute('value', v);
    else this.removeAttribute('value');
  }

  /** The selected record's display label. */
  get display(): string {
    return this.getAttribute('display') ?? '';
  }
  set display(v: string) {
    if (v) this.setAttribute('display', v);
    else this.removeAttribute('display');
  }

  get open(): boolean {
    return !this.listbox.hidden;
  }

  /** Focus the typeahead input (the inline record-detail editor auto-focuses on click-to-edit). */
  override focus(options?: FocusOptions): void {
    this.input.focus(options);
  }

  private shadowContains(node: Element): boolean {
    return this.root.contains(node);
  }

  private sync(): void {
    const labelText = this.getAttribute('label') ?? '';
    this.labelEl.childNodes.forEach((n) => {
      if (n.nodeType === Node.TEXT_NODE) n.remove();
    });
    if (labelText) this.labelEl.insertBefore(document.createTextNode(labelText), this.reqEl);
    this.reqEl.hidden = !this.hasAttribute('required');

    const mode = this.getAttribute('mode') === 'edit' ? 'edit' : 'read';
    const value = this.getAttribute('value') ?? '';
    const display = this.getAttribute('display') ?? '';
    const href = this.getAttribute('href');
    const hasValue = value.length > 0 || display.length > 0;
    if (hasValue) this.setAttribute('data-has-value', '');
    else this.removeAttribute('data-has-value');

    // read view
    this.readView.hidden = mode !== 'read';
    if (hasValue) {
      this.linkEl.hidden = false;
      this.placeholderEl.hidden = true;
      this.linkEl.textContent = display || value;
      if (href) this.linkEl.setAttribute('href', href);
      else this.linkEl.removeAttribute('href');
      // a link with no href is still keyboard-reachable as a re-point trigger
      if (!href) this.linkEl.tabIndex = 0;
    } else {
      this.linkEl.hidden = true;
      this.placeholderEl.hidden = false;
      this.placeholderEl.textContent = this.getAttribute('placeholder') || '—';
    }

    // edit view
    this.combo.hidden = mode !== 'edit';
    this.input.placeholder = this.getAttribute('placeholder') ?? 'Search…';
    this.input.disabled = this.hasAttribute('disabled');
    if (this.hasAttribute('invalid')) this.input.setAttribute('aria-invalid', 'true');
    else this.input.removeAttribute('aria-invalid');
    if (labelText) this.input.setAttribute('aria-label', labelText);
    // reflect the current selection into the input text (without reopening)
    if (document.activeElement !== this && this.input.value === '' && display) {
      this.input.value = display;
    }

    this.messageEl.textContent = this.getAttribute('message') ?? '';
  }

  /** A comma- or space-separated attribute, as the list it names. Absent or blank ⇒ nothing said. */
  private fieldList(attr: string): string[] {
    return (this.getAttribute(attr) ?? '')
      .split(/[,\s]+/)
      .map((name) => name.trim())
      .filter(Boolean);
  }

  /** A whole, non-negative number from an attribute, or the fallback when it does not say one. */
  private numberAttr(attr: string, fallback: number): number {
    const raw = Number(this.getAttribute(attr));
    return Number.isFinite(raw) && raw >= 0 ? Math.floor(raw) : fallback;
  }

  /**
   * How many characters are worth asking about. Zero by default, which is what this control did
   * before: it asked from the first keystroke, and from an empty box.
   */
  private get minChars(): number {
    return this.numberAttr('min-chars', 0);
  }

  private onInput(): void {
    const q = this.input.value;
    if (this.debounce) clearTimeout(this.debounce);
    const delay = this.numberAttr('search-delay', DEFAULT_SEARCH_DELAY);
    this.debounce = setTimeout(() => this.runSearch(q), delay);
  }

  private async runSearch(query: string): Promise<void> {
    if (!this._search) return;

    // CAOS-601 — below the floor the surface set, say what is needed rather than asking for
    // everything and showing a list nobody asked for. A token is still taken so an answer already
    // in flight cannot land after this.
    const minChars = this.minChars;
    if (minChars > 0 && query.trim().length < minChars) {
      this.searchToken += 1;
      this._options = [];
      this.renderStatus(`Type ${minChars} or more characters to search.`);
      this.openList();
      return;
    }

    const token = ++this.searchToken;
    // show a lightweight searching state
    this.renderStatus('Searching…');
    this.openList();
    try {
      const results = await this._search(query, { query, matchFields: this.fieldList('match-fields') });
      if (token !== this.searchToken) return; // a newer query superseded this one
      this._options = results ?? [];
      this.renderOptions();
    } catch {
      if (token !== this.searchToken) return;
      this._options = [];
      this.renderStatus('Could not load results.');
    }
  }

  private renderStatus(text: string): void {
    this.listbox.replaceChildren();
    this.optionEls = [];
    this.activeIndex = -1;
    const li = document.createElement('li');
    li.className = 'status';
    li.setAttribute('role', 'presentation');
    li.textContent = text;
    this.listbox.append(li);
    this.input.removeAttribute('aria-activedescendant');
  }

  private renderOptions(): void {
    this.listbox.replaceChildren();
    this.optionEls = [];
    this.activeIndex = -1;
    if (this._options.length === 0) {
      this.renderStatus('No matches.');
      return;
    }
    this._options.forEach((opt, i) => {
      const li = document.createElement('li');
      li.className = 'option';
      li.setAttribute('part', 'option');
      li.setAttribute('role', 'option');
      li.id = `${this.listboxId}-opt-${i}`;
      li.setAttribute('aria-selected', String(opt.value === this.value));
      const label = document.createElement('span');
      label.className = 'opt-label';
      label.textContent = opt.label;
      li.append(label);
      const secondary = this.secondaryLine(opt);
      if (secondary) {
        const sub = document.createElement('span');
        sub.className = 'opt-sub';
        sub.textContent = secondary;
        li.append(sub);
      }
      li.addEventListener('pointerdown', (e) => {
        e.preventDefault(); // keep focus on the input
        this.selectIndex(i);
      });
      li.addEventListener('pointerenter', () => this.setActive(i));
      this.listbox.append(li);
      this.optionEls.push(li);
    });
    this.input.removeAttribute('aria-activedescendant');
  }

  /**
   * The second line of a result (CAOS-601).
   *
   * An explicit `sublabel` still wins, because a source that has already decided what the second
   * line says has said something the surface did not ask it to reconsider. Otherwise the surface's
   * `display-fields` pick from the option's own fields, in the order it listed them, and anything
   * the option does not carry is skipped rather than drawn as a blank.
   */
  private secondaryLine(opt: CaosLookupOption): string {
    if (opt.sublabel) return opt.sublabel;
    const wanted = this.fieldList('display-fields');
    if (wanted.length === 0 || !opt.fields) return '';
    return wanted
      .map((name) => opt.fields?.[name])
      .filter((v): v is string => typeof v === 'string' && v.trim() !== '')
      .join(' · ');
  }

  private openList(): void {
    if (!this.open) {
      this.listbox.hidden = false;
      this.input.setAttribute('aria-expanded', 'true');
      document.addEventListener('pointerdown', this.onDocPointer, true);
    }
  }

  private close(): void {
    if (!this.open) return;
    this.listbox.hidden = true;
    this.input.setAttribute('aria-expanded', 'false');
    this.input.removeAttribute('aria-activedescendant');
    this.activeIndex = -1;
    document.removeEventListener('pointerdown', this.onDocPointer, true);
  }

  private setActive(index: number): void {
    this.activeIndex = index;
    this.optionEls.forEach((el, i) => el.classList.toggle('active', i === index));
    const active = this.optionEls[index];
    if (active) {
      this.input.setAttribute('aria-activedescendant', active.id);
      active.scrollIntoView({ block: 'nearest' });
    } else {
      this.input.removeAttribute('aria-activedescendant');
    }
  }

  private selectIndex(index: number): void {
    const opt = this._options[index];
    if (!opt) return;
    this.value = opt.value;
    this.display = opt.label;
    this.input.value = opt.label;
    this.close();
    this.dispatchEvent(
      new CustomEvent('caos-lookup-change', {
        bubbles: true,
        composed: true,
        detail: { value: opt.value, label: opt.label },
      }),
    );
  }

  private clear(): void {
    this.value = '';
    this.display = '';
    this.input.value = '';
    this.close();
    this.dispatchEvent(new CustomEvent('caos-lookup-clear', { bubbles: true, composed: true }));
    this.input.focus();
  }

  private onKeydown(e: KeyboardEvent): void {
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        if (!this.open) {
          this.runSearch(this.input.value);
        } else if (this.optionEls.length) {
          this.setActive(this.activeIndex >= this.optionEls.length - 1 ? 0 : this.activeIndex + 1);
        }
        break;
      case 'ArrowUp':
        e.preventDefault();
        if (this.open && this.optionEls.length) {
          this.setActive(this.activeIndex <= 0 ? this.optionEls.length - 1 : this.activeIndex - 1);
        }
        break;
      case 'Home':
        if (this.open && this.optionEls.length) {
          e.preventDefault();
          this.setActive(0);
        }
        break;
      case 'End':
        if (this.open && this.optionEls.length) {
          e.preventDefault();
          this.setActive(this.optionEls.length - 1);
        }
        break;
      case 'Enter':
        if (this.open && this.activeIndex >= 0) {
          e.preventDefault();
          this.selectIndex(this.activeIndex);
        }
        break;
      case 'Escape':
        if (this.open) {
          e.preventDefault();
          this.close();
        }
        break;
    }
  }
}

define('caos-lookup-field', CaosLookupField);

declare global {
  interface HTMLElementTagNameMap {
    'caos-lookup-field': CaosLookupField;
  }
}
